import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { School, Plus, Pencil, Upload, Download, Search, FileSpreadsheet, CheckCircle2, AlertTriangle, Layers, MapPin, Building, UserCheck } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const empty = {
  name: "",
  status: "active",
  school_code: "",
  city: "",
  state: "",
  board: "",
  assigned_coach: "",
  notes: "",
};

export default function SchoolMaster() {
  const [schools, setSchools] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  // Search & Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [coachFilter, setCoachFilter] = useState("all");

  // Import Modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importStats, setImportStats] = useState({ total: 0, newCount: 0, updateCount: 0 });
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/schools").then((r) => setSchools(r.data || []));
  useEffect(() => { load(); }, []);

  // Summary Metrics (computed from actual data)
  const summary = useMemo(() => {
    const total = schools.length;
    const active = schools.filter((s) => s.status === "active").length;
    const inactive = total - active;
    const states = new Set(schools.map((s) => s.state).filter(Boolean)).size;
    const cities = new Set(schools.map((s) => s.city).filter(Boolean)).size;
    const coaches = new Set(schools.map((s) => s.assigned_coach).filter(Boolean)).size;
    return { total, active, inactive, states, cities, coaches };
  }, [schools]);

  // Unique filter option lists
  const filterOptions = useMemo(() => {
    const states = Array.from(new Set(schools.map((s) => s.state).filter(Boolean))).sort();
    const cities = Array.from(new Set(schools.map((s) => s.city).filter(Boolean))).sort();
    const coaches = Array.from(new Set(schools.map((s) => s.assigned_coach).filter(Boolean))).sort();
    return { states, cities, coaches };
  }, [schools]);

  // Filtered schools
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schools.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (stateFilter !== "all" && s.state !== stateFilter) return false;
      if (cityFilter !== "all" && s.city !== cityFilter) return false;
      if (coachFilter !== "all" && s.assigned_coach !== coachFilter) return false;
      if (q) {
        const text = `${s.name || ""} ${s.school_code || ""} ${s.city || ""} ${s.state || ""} ${s.board || ""} ${s.assigned_coach || ""}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [schools, search, statusFilter, stateFilter, cityFilter, coachFilter]);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      status: s.status || "active",
      school_code: s.school_code || "",
      city: s.city || "",
      state: s.state || "",
      board: s.board || "",
      assigned_coach: s.assigned_coach || "",
      notes: s.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("School / Field name is required");
    try {
      if (editing) {
        await api.patch(`/schools/${editing.school_id}`, form);
        toast.success("School updated");
      } else {
        await api.post("/schools", form);
        toast.success("School added");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save school");
    }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/schools/${s.school_id}`, { status: s.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change status");
    }
  };

  // Handle Excel / CSV File Parsing
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: "array" });
        // Pick 01_SCHOOL_MASTER if available, else first sheet
        const sheetName = wb.SheetNames.find((n) => n.toUpperCase().includes("SCHOOL_MASTER") || n.toUpperCase().includes("SCHOOL")) || wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!raw.length) {
          toast.error("The uploaded sheet has no rows.");
          return;
        }

        // Map columns flexible (detect School Name, State, City, Code, Board, Coach)
        const existingNames = new Set(schools.map((s) => (s.name || "").trim().toLowerCase()));
        const existingCodes = new Set(schools.map((s) => (s.school_code || "").trim().toLowerCase()).filter(Boolean));

        const parsed = raw.map((row) => {
          // Normalize row keys to lower
          const kmap = {};
          Object.keys(row).forEach((k) => { kmap[k.trim().toLowerCase()] = row[k]; });

          const name = (
            kmap["school name"] || kmap["school_name"] || kmap["name"] || kmap["school"] || ""
          ).toString().trim();
          const code = (
            kmap["school id"] || kmap["school_id"] || kmap["school code"] || kmap["code"] || ""
          ).toString().trim();
          const city = (
            kmap["city / locality"] || kmap["city"] || kmap["locality"] || ""
          ).toString().trim();
          const state = (
            kmap["state"] || kmap["school state"] || ""
          ).toString().trim();
          const board = (
            kmap["board / curriculum"] || kmap["board"] || kmap["curriculum"] || ""
          ).toString().trim();
          const coach = (
            kmap["coach name"] || kmap["assigned coach"] || kmap["coach"] || ""
          ).toString().trim();
          const rawStatus = (
            kmap["school status"] || kmap["status"] || ""
          ).toString().trim().toLowerCase();
          const status = (rawStatus.includes("inactive") || rawStatus.includes("lost") || rawStatus.includes("dropped")) ? "inactive" : "active";

          const isDuplicate = existingNames.has(name.toLowerCase()) || (code && existingCodes.has(code.toLowerCase()));

          return {
            name,
            school_code: code,
            city,
            state,
            board,
            assigned_coach: coach,
            status,
            isDuplicate,
          };
        }).filter((r) => r.name); // only rows with a name

        if (!parsed.length) {
          toast.error("Could not find a 'School Name' column in the sheet.");
          return;
        }

        const newCount = parsed.filter((r) => !r.isDuplicate).length;
        const updateCount = parsed.filter((r) => r.isDuplicate).length;
        setImportRows(parsed);
        setImportStats({ total: parsed.length, newCount, updateCount });
        setImportOpen(true);
      } catch (err) {
        toast.error("Failed to parse file: " + (err.message || "Invalid Excel/CSV"));
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const confirmImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const payload = {
        schools: importRows.map((r) => ({
          name: r.name,
          school_code: r.school_code || null,
          city: r.city || null,
          state: r.state || null,
          board: r.board || null,
          assigned_coach: r.assigned_coach || null,
          status: r.status || "active",
        })),
      };
      const res = await api.post("/schools/import", payload);
      toast.success(`Import complete: ${res.data.created} added, ${res.data.updated} updated.`);
      setImportOpen(false);
      setImportRows([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="school-master-page">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-display tracking-tight flex items-center gap-2">
            <School className="h-6 w-6 text-primary" /> Manage Schools
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Master repository of schools &amp; field locations. Active schools are automatically available for Daily Tracker activity selection.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Hidden File Input for Excel/CSV */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="school-import-file-input"
          />
          <Button
            data-testid="import-schools-btn"
            variant="outline"
            className="gap-1.5"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4" /> Import Excel / CSV
          </Button>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-school-button" onClick={openNew} className="gap-1.5">
                <Plus className="h-4 w-4" /> Add School / Field
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editing ? "Edit School / Field" : "Add School / Field"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>School / Field Name</Label>
                  <Input
                    data-testid="school-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1"
                    placeholder="e.g. Jain Vidyalaya School"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>School Code (optional)</Label>
                    <Input
                      data-testid="school-code-input"
                      value={form.school_code}
                      onChange={(e) => setForm({ ...form, school_code: e.target.value })}
                      className="mt-1"
                      placeholder="e.g. LLE/LIM/0004"
                    />
                  </div>
                  <div>
                    <Label>Board / Curriculum</Label>
                    <Input
                      data-testid="school-board-input"
                      value={form.board}
                      onChange={(e) => setForm({ ...form, board: e.target.value })}
                      className="mt-1"
                      placeholder="e.g. CBSE / State Board"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>City / Locality</Label>
                    <Input
                      data-testid="school-city-input"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="mt-1"
                      placeholder="e.g. Madurai"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      data-testid="school-state-input"
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                      className="mt-1"
                      placeholder="e.g. Tamil Nadu"
                    />
                  </div>
                </div>
                <div>
                  <Label>Assigned Coach / Allocation (optional)</Label>
                  <Input
                    data-testid="school-coach-input"
                    value={form.assigned_coach}
                    onChange={(e) => setForm({ ...form, assigned_coach: e.target.value })}
                    className="mt-1"
                    placeholder="e.g. Anand Babu"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <span className="text-sm font-medium block">Active</span>
                    <span className="text-[11px] text-muted-foreground block">Inactive schools are hidden from Daily Tracker selection but preserved in history.</span>
                  </div>
                  <Switch
                    data-testid="school-status-switch"
                    checked={form.status === "active"}
                    onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-school-button" onClick={save}>
                  {editing ? "Save changes" : "Add School"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Compact Top Summary (Real Data Only) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="schools-summary">
        <div className="rounded-xl border border-border bg-card p-3.5">
          <p className="text-2xl font-bold font-display" data-testid="summary-total">{summary.total}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 mt-0.5">
            <School className="h-3 w-3" /> Total Schools
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3.5">
          <p className="text-2xl font-bold font-display text-emerald-600" data-testid="summary-active">{summary.active}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 mt-0.5">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Active
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3.5">
          <p className="text-2xl font-bold font-display text-zinc-500" data-testid="summary-inactive">{summary.inactive}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 mt-0.5">
            <AlertTriangle className="h-3 w-3" /> Inactive
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3.5">
          <p className="text-2xl font-bold font-display" data-testid="summary-states">{summary.states}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 mt-0.5">
            <Layers className="h-3 w-3" /> States
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3.5">
          <p className="text-2xl font-bold font-display" data-testid="summary-cities">{summary.cities}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 mt-0.5">
            <Building className="h-3 w-3" /> Cities
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3.5">
          <p className="text-2xl font-bold font-display text-primary" data-testid="summary-coaches">{summary.coaches}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1 mt-0.5">
            <UserCheck className="h-3 w-3" /> Coaches
          </p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-2.5 flex-wrap bg-card border border-border p-3 rounded-xl" data-testid="schools-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="search-school-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search school name, code, city, coach…"
            className="pl-9 h-9 text-xs"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status-select" className="h-9 w-32 text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        {filterOptions.states.length > 0 && (
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger data-testid="filter-state-select" className="h-9 w-36 text-xs">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {filterOptions.states.map((st) => (
                <SelectItem key={st} value={st}>{st}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {filterOptions.cities.length > 0 && (
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger data-testid="filter-city-select" className="h-9 w-36 text-xs">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {filterOptions.cities.map((ct) => (
                <SelectItem key={ct} value={ct}>{ct}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {filterOptions.coaches.length > 0 && (
          <Select value={coachFilter} onValueChange={setCoachFilter}>
            <SelectTrigger data-testid="filter-coach-select" className="h-9 w-40 text-xs">
              <SelectValue placeholder="Coach" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Coaches</SelectItem>
              {filterOptions.coaches.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(search || statusFilter !== "all" || stateFilter !== "all" || cityFilter !== "all" || coachFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearch(""); setStatusFilter("all"); setStateFilter("all"); setCityFilter("all"); setCoachFilter("all"); }}
            className="h-9 text-xs text-muted-foreground"
          >
            Clear
          </Button>
        )}
      </div>

      {/* School List Table */}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <th className="text-left font-semibold px-4 py-3">Code</th>
              <th className="text-left font-semibold px-4 py-3">School / Field Name</th>
              <th className="text-left font-semibold px-4 py-3">City</th>
              <th className="text-left font-semibold px-4 py-3">State</th>
              <th className="text-left font-semibold px-4 py-3">Board</th>
              <th className="text-left font-semibold px-4 py-3">Assigned Coach</th>
              <th className="text-left font-semibold px-4 py-3">Status</th>
              <th className="text-right font-semibold px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  <School className="h-8 w-8 mx-auto opacity-40 mb-2" />
                  No schools found matching your search.
                </td>
              </tr>
            ) : filtered.map((s) => (
              <tr key={s.school_id} data-testid={`school-row-${s.school_id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.school_code || "—"}</td>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{s.city || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{s.state || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{s.board || "—"}</td>
                <td className="px-4 py-3 text-xs">{s.assigned_coach || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"}`}>
                    {s.status === "active" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-school-${s.school_id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <Button data-testid={`toggle-school-${s.school_id}`} onClick={() => toggle(s)} size="sm" variant="outline" className="h-7 text-xs">
                      {s.status === "active" ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Import Preview Modal */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="school-import-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" /> Import Schools Preview
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-muted/40 p-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xl font-bold font-display">{importStats.total}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total In File</p>
              </div>
              <div>
                <p className="text-xl font-bold font-display text-emerald-600">{importStats.newCount}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">New Records</p>
              </div>
              <div>
                <p className="text-xl font-bold font-display text-blue-600">{importStats.updateCount}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Existing (Will Update)</p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Review parsed rows below. Existing schools (matched by name or code) will be updated with file details — no records are deleted.
            </p>

            <div className="rounded-xl border border-border max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/60 sticky top-0 border-b border-border">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Match</th>
                    <th className="px-3 py-2 text-left font-semibold">Code</th>
                    <th className="px-3 py-2 text-left font-semibold">School Name</th>
                    <th className="px-3 py-2 text-left font-semibold">City</th>
                    <th className="px-3 py-2 text-left font-semibold">State</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/20">
                      <td className="px-3 py-1.5">
                        {r.isDuplicate ? (
                          <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">Update</span>
                        ) : (
                          <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700">New</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono">{r.school_code || "—"}</td>
                      <td className="px-3 py-1.5 font-medium">{r.name}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.city || "—"}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.state || "—"}</td>
                      <td className="px-3 py-1.5">{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {importRows.length > 50 && (
              <p className="text-[11px] text-muted-foreground italic text-center">
                Showing first 50 of {importRows.length} schools. All {importRows.length} will be imported on confirm.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>
              Cancel
            </Button>
            <Button data-testid="confirm-import-btn" onClick={confirmImport} disabled={importing} className="gap-1.5">
              {importing ? "Importing…" : `Confirm Import (${importRows.length} Schools)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
